import React, { useState, useEffect, useCallback, useRef } from 'react';
import { diseaseStateReportService, DiseaseStateReportData } from '../lib/diseaseStateReportService';
import { exportService } from '../lib/exportService';
import { ReportDisclaimer, REPORT_TOOLTIP_DISCLAIMER } from '../components/reports/ReportDisclaimer';
import { EmptyState } from '../components/common/EmptyState';
import { Skeleton } from '../components/common/Skeleton';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Dot
} from 'recharts';
import { format, subDays, parseISO } from 'date-fns';
import { pl } from 'date-fns/locale';

export interface DiseaseStateReportProps {
  user: any;
  onBack?: () => void;
  /** Callback do nawigacji - np. do sekcji wypróżnień przy braku danych */
  onNavigate?: (section: string) => void;
}

// Funkcja pomocnicza do rem()
const rem = (px: number) => `${px / 16}rem`;

const fontSize = {
  xs: rem(12),
  sm: rem(14),
  base: rem(16),
  lg: rem(18),
  xl: rem(20),
  '2xl': rem(24)
};

const TrendUpIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
    <polyline points="17 6 23 6 23 12"/>
  </svg>
);

const TrendDownIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/>
    <polyline points="17 18 23 18 23 12"/>
  </svg>
);


const ChartIconEmpty = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="20" x2="12" y2="10"/>
    <line x1="18" y1="20" x2="18" y2="4"/>
    <line x1="6" y1="20" x2="6" y2="16"/>
  </svg>
);

export const DiseaseStateReport = ({ user, onBack, onNavigate }: DiseaseStateReportProps) => {
  const reportExportRef = useRef<HTMLDivElement>(null);
  const [report, setReport] = useState<DiseaseStateReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [periodFilter, setPeriodFilter] = useState<'14' | '30' | '90' | 'custom'>('30');
  const [customDateFrom, setCustomDateFrom] = useState<string>('');
  const [customDateTo, setCustomDateTo] = useState<string>('');
  
  const calculateDateRange = useCallback((period: string) => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    
    if (period === 'custom') {
      return {
        dateFrom: customDateFrom || format(subDays(today, 30), 'yyyy-MM-dd'),
        dateTo: customDateTo || format(today, 'yyyy-MM-dd')
      };
    }
    
    const days = parseInt(period);
    const dateFrom = format(subDays(today, days - 1), 'yyyy-MM-dd');
    const dateTo = format(today, 'yyyy-MM-dd');
    
    return { dateFrom, dateTo };
  }, [customDateFrom, customDateTo]);
  
  const loadReport = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      const { dateFrom, dateTo } = calculateDateRange(periodFilter);
      const data = await diseaseStateReportService.getDiseaseStateReport(user.id, dateFrom, dateTo);
      setReport(data);
    } catch (error) {
      console.error('Błąd ładowania raportu:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.id, periodFilter, calculateDateRange]);
  
  useEffect(() => {
    loadReport();
  }, [loadReport]);
  
  // Oblicz trendy dla KPI (porównanie ostatnie 7 vs poprzednie 7 dni)
  const getTrend = useCallback((current: number, previous: number): { value: number; direction: 'up' | 'down' | 'stable' } => {
    if (previous === 0) return { value: 0, direction: 'stable' };
    const change = ((current - previous) / previous) * 100;
    if (Math.abs(change) < 5) return { value: Math.abs(change), direction: 'stable' };
    return {
      value: Math.abs(change),
      direction: change > 0 ? 'up' : 'down'
    };
  }, []);
  
  const last7Days = report?.daySummaries.slice(-7) || [];
  const previous7Days = (report?.daySummaries.length || 0) > 7 
    ? report?.daySummaries.slice(-14, -7) || []
    : [];
  
  const last7AvgBm = last7Days.length > 0
    ? last7Days.reduce((sum, d) => sum + d.bmCount, 0) / last7Days.length
    : 0;
  const prev7AvgBm = previous7Days.length > 0
    ? previous7Days.reduce((sum, d) => sum + d.bmCount, 0) / previous7Days.length
    : 0;
  
  const last7BloodDays = last7Days.filter(d => d.bloodPresentDay).length;
  const prev7BloodDays = previous7Days.filter(d => d.bloodPresentDay).length;
  
  const last7AvgPain = last7Days.length > 0 && last7Days.some(d => d.painAvgDay > 0)
    ? last7Days.filter(d => d.painAvgDay > 0).reduce((sum, d) => sum + d.painAvgDay, 0) / last7Days.filter(d => d.painAvgDay > 0).length
    : 0;
  const prev7AvgPain = previous7Days.length > 0 && previous7Days.some(d => d.painAvgDay > 0)
    ? previous7Days.filter(d => d.painAvgDay > 0).reduce((sum, d) => sum + d.painAvgDay, 0) / previous7Days.filter(d => d.painAvgDay > 0).length
    : 0;
  
  const last7AvgUrgency = last7Days.length > 0
    ? last7Days.reduce((sum, d) => sum + d.urgencyAvgDay, 0) / last7Days.length
    : 0;
  const prev7AvgUrgency = previous7Days.length > 0
    ? previous7Days.reduce((sum, d) => sum + d.urgencyAvgDay, 0) / previous7Days.length
    : 0;
  
  const last7MucusDays = last7Days.filter(d => d.mucusPresentDay).length;
  const prev7MucusDays = previous7Days.filter(d => d.mucusPresentDay).length;
  const last7TotalBm = last7Days.reduce((s, d) => s + d.bmCount, 0);
  const prev7TotalBm = previous7Days.reduce((s, d) => s + d.bmCount, 0);
  const last7LiquidBm = last7Days.reduce((s, d) => s + (d.liquidBmCount ?? d.consistencyDistribution?.płynna ?? 0), 0);
  const prev7LiquidBm = previous7Days.reduce((s, d) => s + (d.liquidBmCount ?? d.consistencyDistribution?.płynna ?? 0), 0);
  const last7LiquidPercent = last7TotalBm > 0 ? Math.round((last7LiquidBm / last7TotalBm) * 100) : 0;
  const prev7LiquidPercent = prev7TotalBm > 0 ? Math.round((prev7LiquidBm / prev7TotalBm) * 100) : 0;

  const bmTrend = getTrend(last7AvgBm, prev7AvgBm);
  const bloodTrend = getTrend(last7BloodDays, prev7BloodDays);
  const painTrend = getTrend(last7AvgPain, prev7AvgPain);
  const urgencyTrend = getTrend(last7AvgUrgency, prev7AvgUrgency);
  const mucusTrend = getTrend(last7MucusDays, prev7MucusDays);
  const liquidTrend = getTrend(last7LiquidPercent, prev7LiquidPercent);
  
  // Przygotuj dane do wykresu
  const chartData = report?.daySummaries.map(day => ({
    date: format(parseISO(day.date), 'dd.MM'),
    fullDate: day.date,
    severityScore: day.severityScore,
    hasBlood: day.bloodPresentDay,
    bmCount: day.bmCount,
    painAvg: day.painAvgDay,
    urgencyAvg: day.urgencyAvgDay,
    bloodMax: day.bloodMaxAmount,
    mucusPresent: day.mucusPresentDay,
    mucusMax: day.mucusMaxAmount,
    liquidPercent: day.liquidBmPercent ?? 0
  })) || [];
  
  if (loading) {
    return (
      <div style={{ padding: rem(24) }}>
        {onBack && (
          <button
            onClick={onBack}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: rem(8),
              marginBottom: rem(24),
              padding: `${rem(8)} ${rem(12)}`,
              background: 'transparent',
              border: '1px solid #E5E5E5',
              borderRadius: rem(8),
              color: '#666666',
              fontSize: fontSize.sm,
              cursor: 'pointer',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="19" y1="12" x2="5" y2="12"/>
              <polyline points="12 19 5 12 12 5"/>
            </svg>
            Wróć do raportów
          </button>
        )}
        <Skeleton width="100%" height="200px" borderRadius="16px" />
        <div style={{ height: rem(16) }} />
        <Skeleton width="100%" height="120px" borderRadius="16px" />
        <div style={{ height: rem(16) }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: rem(12) }}>
          <Skeleton height="80px" borderRadius="12px" />
          <Skeleton height="80px" borderRadius="12px" />
          <Skeleton height="80px" borderRadius="12px" />
        </div>
      </div>
    );
  }
  
  const hasInsufficientData = !report || (report.daySummaries && report.daySummaries.length === 0);
  const daysOfData = report?.daySummaries?.length ?? 0;

  if (hasInsufficientData) {
    return (
      <div style={{ padding: rem(24) }}>
        {onBack && (
          <button
            onClick={onBack}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: rem(8),
              marginBottom: rem(24),
              padding: `${rem(8)} ${rem(12)}`,
              background: 'transparent',
              border: '1px solid #E5E5E5',
              borderRadius: rem(8),
              color: '#666666',
              fontSize: fontSize.sm,
              cursor: 'pointer',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="19" y1="12" x2="5" y2="12"/>
              <polyline points="12 19 5 12 12 5"/>
            </svg>
            Wróć do raportów
          </button>
        )}
        <EmptyState
          icon={<ChartIconEmpty />}
          title="Za mało danych do wygenerowania raportu"
          description={`Potrzebujesz co najmniej 14 dni danych aby wygenerować wiarygodny raport. Obecnie masz ${daysOfData} dni.`}
          actionLabel="Dodaj więcej wpisów"
          onAction={() => onNavigate?.('bowel')}
        />
      </div>
    );
  }
  
  const { dateFrom, dateTo } = calculateDateRange(periodFilter);
  
  const ArrowLeftIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="19" y1="12" x2="5" y2="12"/>
      <polyline points="12 19 5 12 12 5"/>
    </svg>
  );

  return (
    <div className="report-export-area" style={{ padding: rem(24) }} ref={reportExportRef}>
      {/* Globalny disclaimer dla wszystkich raportów */}
      {/* TODO: Każdy nowy raport MUSI używać komponentu ReportDisclaimer jako pierwszego elementu UI */}
      <ReportDisclaimer />
      
      {/* Header */}
      <div style={{ marginBottom: rem(24), display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
        {onBack && (
          <button
            className="no-pdf-export"
            onClick={onBack}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: rem(8),
              marginBottom: rem(16),
              padding: `${rem(8)} ${rem(12)}`,
              background: 'transparent',
              border: '1px solid #E5E5E5',
              borderRadius: rem(8),
              color: '#666666',
              fontSize: fontSize.sm,
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#6B7FFF';
              e.currentTarget.style.color = '#6B7FFF';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#E5E5E5';
              e.currentTarget.style.color = '#666666';
            }}
          >
            <ArrowLeftIcon />
            <span>Wróć do raportów</span>
          </button>
        )}
        <h1 style={{ 
          fontSize: fontSize['2xl'], 
          fontWeight: 600, 
          color: '#1A1A1A', 
          margin: 0,
          marginBottom: rem(8)
        }}>
          Stan choroby
        </h1>
        <p style={{ 
          fontSize: fontSize.sm, 
          color: '#999999', 
          margin: 0 
        }}>
          Analiza na podstawie wypróżnień, samopoczucia i wyników badań
        </p>
        </div>
        
        {/* Przycisk Pobierz raport */}
        {report && (
          <button
            className="no-pdf-export"
            onClick={async () => {
              if (!reportExportRef.current || exporting) return;
              setExporting(true);
              try {
                await exportService.exportReportElementToPDF(reportExportRef.current, 'raport_stan_choroby');
              } finally {
                setExporting(false);
              }
            }}
            disabled={exporting}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: rem(8),
              padding: `${rem(10)} ${rem(20)}`,
              background: 'linear-gradient(135deg, #6B7FFF 0%, #8B5CF6 100%)',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: rem(12),
              fontSize: fontSize.sm,
              fontWeight: 600,
              cursor: exporting ? 'wait' : 'pointer',
              boxShadow: '0 2px 8px rgba(107, 127, 255, 0.3)',
              transition: 'all 0.2s ease',
              opacity: exporting ? 0.8 : 1
            }}
            onMouseEnter={(e) => {
              if (!exporting) {
                e.currentTarget.style.opacity = '0.9';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = exporting ? '0.6' : '1';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            {exporting ? 'Pobieranie…' : 'Pobierz raport'}
          </button>
        )}
      </div>
      
      {/* Filtry okresu */}
      <div style={{ 
        marginBottom: rem(24),
        display: 'flex',
        flexWrap: 'wrap',
        gap: rem(12),
        alignItems: 'center'
      }}>
        <div style={{
          display: 'flex',
          gap: rem(8),
          background: '#F5F5F5',
          padding: rem(4),
          borderRadius: rem(12),
          border: '1px solid #E5E5E5'
        }}>
          {(['14', '30', '90'] as const).map(period => (
            <button
              key={period}
              onClick={() => setPeriodFilter(period)}
              style={{
                padding: `${rem(8)} ${rem(16)}`,
                borderRadius: rem(8),
                border: 'none',
                background: periodFilter === period ? '#FFFFFF' : 'transparent',
                color: periodFilter === period ? '#1A1A1A' : '#999999',
                fontSize: fontSize.sm,
                fontWeight: periodFilter === period ? 600 : 400,
                cursor: 'pointer',
                boxShadow: periodFilter === period ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
              }}
            >
              {period} dni
            </button>
          ))}
          <button
            onClick={() => setPeriodFilter('custom')}
            style={{
              padding: `${rem(8)} ${rem(16)}`,
              borderRadius: rem(8),
              border: 'none',
              background: periodFilter === 'custom' ? '#FFFFFF' : 'transparent',
              color: periodFilter === 'custom' ? '#1A1A1A' : '#999999',
              fontSize: fontSize.sm,
              fontWeight: periodFilter === 'custom' ? 600 : 400,
              cursor: 'pointer',
              boxShadow: periodFilter === 'custom' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
            }}
          >
            Własny zakres
          </button>
        </div>
        
        {periodFilter === 'custom' && (
          <div style={{ display: 'flex', gap: rem(8), alignItems: 'center' }}>
            <input
              type="date"
              value={customDateFrom}
              onChange={(e) => setCustomDateFrom(e.target.value)}
              style={{
                padding: `${rem(8)} ${rem(12)}`,
                borderRadius: rem(8),
                border: '1px solid #E5E5E5',
                fontSize: fontSize.sm,
                color: '#1A1A1A'
              }}
            />
            <span style={{ color: '#999999' }}>—</span>
            <input
              type="date"
              value={customDateTo}
              onChange={(e) => setCustomDateTo(e.target.value)}
              style={{
                padding: `${rem(8)} ${rem(12)}`,
                borderRadius: rem(8),
                border: '1px solid #E5E5E5',
                fontSize: fontSize.sm,
                color: '#1A1A1A'
              }}
            />
          </div>
        )}
        
        <div style={{ marginLeft: 'auto', fontSize: fontSize.sm, color: '#999999' }}>
          {format(parseISO(dateFrom), 'dd.MM.yyyy', { locale: pl })} — {format(parseISO(dateTo), 'dd.MM.yyyy', { locale: pl })}
        </div>
      </div>
      
      {/* Badge stanu + Tooltip */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: rem(16),
        marginBottom: rem(24)
      }}>
        {/* Badge stanu */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: rem(12),
          flex: 1
        }}>
          <div style={{
            padding: `${rem(12)} ${rem(20)}`,
            borderRadius: rem(12),
            background: report.classification.state === 'REMISSION' 
              ? 'linear-gradient(135deg, #00A86B 0%, #00C896 100%)'
              : report.classification.state === 'FLARE'
              ? 'linear-gradient(135deg, #FF4444 0%, #FF6666 100%)'
              : 'linear-gradient(135deg, #6B7FFF 0%, #8B5CF6 100%)',
            color: '#FFFFFF',
            fontSize: fontSize.lg,
            fontWeight: 600,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}>
            {report.classification.stateLabel}
          </div>
          
          {/* Trend label */}
          {report.classification.tendency !== 'STABLE' && (
            <div style={{
              padding: `${rem(8)} ${rem(16)}`,
              borderRadius: rem(8),
              background: '#F5F5F5',
              border: '1px solid #E5E5E5',
              fontSize: fontSize.sm,
              color: '#666666',
              fontWeight: 500
            }}>
              {report.classification.tendencyLabel.toLowerCase()}
            </div>
          )}
          
          {/* Tooltip "Jak to liczymy?" */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => {
                alert(`${REPORT_TOOLTIP_DISCLAIMER}\n\nStan jest wyliczany na podstawie: krwi, liczby wypróżnień, pilności, bólu (0–5), konsystencji stolca, śluzu oraz objawów jelitowych z samopoczucia (przelewanie, wzdęcia, ból jelit, gazy).`);
              }}
              style={{
                width: rem(24),
                height: rem(24),
                borderRadius: '50%',
                background: '#F5F5F5',
                border: '1px solid #E5E5E5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: '#666666',
                fontSize: fontSize.sm,
                fontWeight: 600,
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#E5E5E5';
                e.currentTarget.style.color = '#1A1A1A';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#F5F5F5';
                e.currentTarget.style.color = '#666666';
              }}
              title="Jak to liczymy?"
            >
              i
            </button>
          </div>
        </div>
      </div>
      
      {/* Sekcja "Dlaczego taki stan?" */}
      {report.classification.reasons.length > 0 && (
        <div className="report-card" style={{
          background: '#F9F9F9',
          border: '1px solid #E5E5E5',
          borderRadius: rem(12),
          padding: rem(16),
          marginBottom: rem(24)
        }}>
          <div style={{
            fontSize: fontSize.sm,
            fontWeight: 600,
            color: '#666666',
            marginBottom: rem(12)
          }}>
            Dlaczego taki stan?
          </div>
          <ul style={{
            margin: 0,
            paddingLeft: rem(20),
            listStyle: 'disc',
            display: 'flex',
            flexDirection: 'column',
            gap: rem(8)
          }}>
            {report.classification.reasons.map((reason, index) => (
              <li key={index} style={{
                fontSize: fontSize.sm,
                color: '#666666',
                lineHeight: 1.5
              }}>
                {reason}
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Executive Summary */}
      <div className="report-card" style={{
        background: 'linear-gradient(135deg, #6B7FFF 0%, #8B5CF6 100%)',
        borderRadius: rem(16),
        padding: rem(24),
        marginBottom: rem(24),
        color: '#FFFFFF'
      }}>
        <div style={{ fontSize: fontSize.sm, opacity: 0.9, marginBottom: rem(8) }}>
          Podsumowanie
        </div>
        <div style={{ fontSize: fontSize.lg, fontWeight: 600, lineHeight: 1.5 }}>
          Ostatnie {periodFilter === 'custom' ? `${Math.ceil((parseISO(dateTo).getTime() - parseISO(dateFrom).getTime()) / (1000 * 60 * 60 * 24))} dni` : `${periodFilter} dni`}: {report.summaryText}
        </div>
      </div>
      
      {/* KPI Kafelki */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: rem(16),
        marginBottom: rem(24)
      }}>
        {/* Wypróżnienia/dzień */}
        <div className="kpi-card report-card" style={{
          background: '#FFFFFF',
          border: '1px solid #E5E5E5',
          borderRadius: rem(12),
          padding: rem(16)
        }}>
          <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(8) }}>
            Wypróżnienia/dzień
          </div>
          <div style={{ fontSize: fontSize['2xl'], fontWeight: 600, color: '#1A1A1A', marginBottom: rem(8) }}>
            {report.periodMetrics.avgBmPerDay > 0 
              ? report.periodMetrics.avgBmPerDay.toFixed(1) 
              : '—'}
          </div>
          {bmTrend.direction !== 'stable' && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: rem(4),
              fontSize: fontSize.xs,
              color: bmTrend.direction === 'down' ? '#00A86B' : '#FF4444'
            }}>
              {bmTrend.direction === 'down' ? <TrendDownIcon /> : <TrendUpIcon />}
              {bmTrend.value.toFixed(0)}%
            </div>
          )}
        </div>
        
        {/* Dni z krwią */}
        <div style={{
          background: '#FFFFFF',
          border: '1px solid #E5E5E5',
          borderRadius: rem(12),
          padding: rem(16)
        }}>
          <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(8) }}>
            Dni z krwią
          </div>
          <div style={{ fontSize: fontSize['2xl'], fontWeight: 600, color: '#1A1A1A', marginBottom: rem(8) }}>
            {report.periodMetrics.daysTotal > 0
              ? `${Math.round((report.periodMetrics.daysWithBlood / report.periodMetrics.daysTotal) * 100)}%`
              : '—'}
          </div>
          {bloodTrend.direction !== 'stable' && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: rem(4),
              fontSize: fontSize.xs,
              color: bloodTrend.direction === 'down' ? '#00A86B' : '#FF4444'
            }}>
              {bloodTrend.direction === 'down' ? <TrendDownIcon /> : <TrendUpIcon />}
              {bloodTrend.value.toFixed(0)}%
            </div>
          )}
        </div>
        
        {/* Ból */}
        <div className="kpi-card report-card" style={{
          background: '#FFFFFF',
          border: '1px solid #E5E5E5',
          borderRadius: rem(12),
          padding: rem(16)
        }}>
          <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(8) }}>
            Ból
          </div>
          <div style={{ fontSize: fontSize['2xl'], fontWeight: 600, color: '#1A1A1A', marginBottom: rem(8) }}>
            {report.periodMetrics.avgPain > 0 
              ? report.periodMetrics.avgPain.toFixed(1) 
              : '—'}
          </div>
          {painTrend.direction !== 'stable' && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: rem(4),
              fontSize: fontSize.xs,
              color: painTrend.direction === 'down' ? '#00A86B' : '#FF4444'
            }}>
              {painTrend.direction === 'down' ? <TrendDownIcon /> : <TrendUpIcon />}
              {painTrend.value.toFixed(0)}%
            </div>
          )}
        </div>
        
        {/* Pilność */}
        <div className="kpi-card report-card" style={{
          background: '#FFFFFF',
          border: '1px solid #E5E5E5',
          borderRadius: rem(12),
          padding: rem(16)
        }}>
          <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(8) }}>
            Pilność
          </div>
          <div style={{ fontSize: fontSize['2xl'], fontWeight: 600, color: '#1A1A1A', marginBottom: rem(8) }}>
            {report.periodMetrics.avgUrgency > 0 
              ? report.periodMetrics.avgUrgency.toFixed(1) 
              : '—'}
          </div>
          {urgencyTrend.direction !== 'stable' && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: rem(4),
              fontSize: fontSize.xs,
              color: urgencyTrend.direction === 'down' ? '#00A86B' : '#FF4444'
            }}>
              {urgencyTrend.direction === 'down' ? <TrendDownIcon /> : <TrendUpIcon />}
              {urgencyTrend.value.toFixed(0)}%
            </div>
          )}
        </div>
        
        {/* Śluz */}
        <div className="kpi-card report-card" style={{
          background: '#FFFFFF',
          border: '1px solid #E5E5E5',
          borderRadius: rem(12),
          padding: rem(16)
        }}>
          <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(8) }}>
            Śluz
          </div>
          <div style={{ fontSize: fontSize['2xl'], fontWeight: 600, color: '#1A1A1A', marginBottom: rem(8) }}>
            {report.periodMetrics.daysTotal > 0
              ? `${report.periodMetrics.mucusDaysPercent ?? 0}%`
              : '—'}
          </div>
          {mucusTrend.direction !== 'stable' && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: rem(4),
              fontSize: fontSize.xs,
              color: mucusTrend.direction === 'down' ? '#00A86B' : '#FF4444'
            }}>
              {mucusTrend.direction === 'down' ? <TrendDownIcon /> : <TrendUpIcon />}
              {mucusTrend.value.toFixed(0)}%
            </div>
          )}
        </div>
        
        {/* Konsystencja płynna */}
        <div className="kpi-card report-card" style={{
          background: '#FFFFFF',
          border: '1px solid #E5E5E5',
          borderRadius: rem(12),
          padding: rem(16)
        }}>
          <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(8) }}>
            Konsystencja płynna
          </div>
          <div style={{ fontSize: fontSize['2xl'], fontWeight: 600, color: '#1A1A1A', marginBottom: rem(8) }}>
            {report.daySummaries.reduce((s, d) => s + d.bmCount, 0) > 0
              ? `${report.periodMetrics.liquidStoolPercent ?? 0}%`
              : '—'}
          </div>
          {report.daySummaries.reduce((s, d) => s + d.bmCount, 0) > 0 && liquidTrend.direction !== 'stable' && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: rem(4),
              fontSize: fontSize.xs,
              color: liquidTrend.direction === 'down' ? '#00A86B' : '#FF4444'
            }}>
              {liquidTrend.direction === 'down' ? <TrendDownIcon /> : <TrendUpIcon />}
              {liquidTrend.value.toFixed(0)}%
            </div>
          )}
        </div>
        
        {/* Objawy jelitowe */}
        <div className="kpi-card report-card" style={{
          background: '#FFFFFF',
          border: '1px solid #E5E5E5',
          borderRadius: rem(12),
          padding: rem(16)
        }}>
          <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(8) }}>
            Objawy jelitowe
          </div>
          {(report.periodMetrics.avgGurgling === 0 && report.periodMetrics.avgBloating === 0 &&
            report.periodMetrics.avgIntestinalPain === 0 && report.periodMetrics.avgGasLevel === 0) ? (
            <div style={{ fontSize: fontSize.sm, color: '#999999' }}>
              Brak objawów jelitowych w tym okresie
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: rem(8),
              fontSize: fontSize.xs
            }}>
              <div>
                <div style={{ color: '#999999' }}>Przelewanie</div>
                <div style={{ fontWeight: 600, color: '#1A1A1A' }}>{(report.periodMetrics.avgGurgling ?? 0).toFixed(1)}/5</div>
              </div>
              <div>
                <div style={{ color: '#999999' }}>Wzdęcia</div>
                <div style={{ fontWeight: 600, color: '#1A1A1A' }}>{(report.periodMetrics.avgBloating ?? 0).toFixed(1)}/5</div>
              </div>
              <div>
                <div style={{ color: '#999999' }}>Ból jelit</div>
                <div style={{ fontWeight: 600, color: '#1A1A1A' }}>{(report.periodMetrics.avgIntestinalPain ?? 0).toFixed(1)}/5</div>
              </div>
              <div>
                <div style={{ color: '#999999' }}>Gazy</div>
                <div style={{ fontWeight: 600, color: '#1A1A1A' }}>{(report.periodMetrics.avgGasLevel ?? 0).toFixed(1)}/5</div>
              </div>
            </div>
          )}
        </div>
        
        {/* Najgorszy dzień */}
        {report.periodMetrics.worstDay && (
          <div style={{
            background: '#FFFFFF',
            border: '1px solid #E5E5E5',
            borderRadius: rem(12),
            padding: rem(16)
          }}>
            <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(8) }}>
              Najgorszy dzień
            </div>
            <div style={{ fontSize: fontSize.base, fontWeight: 600, color: '#1A1A1A', marginBottom: rem(4) }}>
              {format(parseISO(report.periodMetrics.worstDay.date), 'dd.MM.yyyy', { locale: pl })}
            </div>
            <div style={{ fontSize: fontSize.sm, color: '#FF4444' }}>
              Trend: {report.periodMetrics.worstDay.score}
            </div>
          </div>
        )}
        
        {/* Najlepszy dzień */}
        {report.periodMetrics.bestDay && (
          <div className="kpi-card report-card" style={{
            background: '#FFFFFF',
            border: '1px solid #E5E5E5',
            borderRadius: rem(12),
            padding: rem(16)
          }}>
            <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(8) }}>
              Najlepszy dzień
            </div>
            <div style={{ fontSize: fontSize.base, fontWeight: 600, color: '#1A1A1A', marginBottom: rem(4) }}>
              {format(parseISO(report.periodMetrics.bestDay.date), 'dd.MM.yyyy', { locale: pl })}
            </div>
            <div style={{ fontSize: fontSize.sm, color: '#00A86B' }}>
              Trend: {report.periodMetrics.bestDay.score}
            </div>
          </div>
        )}
      </div>
      
      {/* Samopoczucie */}
      {report.periodMetrics.wellbeingSummary && report.periodMetrics.wellbeingSummary.daysWithData > 0 && (
        <div className="report-card" style={{
          background: '#FFFFFF',
          border: '1px solid #E5E5E5',
          borderRadius: rem(12),
          padding: rem(24),
          marginBottom: rem(24)
        }}>
          <h3 style={{ fontSize: fontSize.lg, fontWeight: 600, color: '#1A1A1A', margin: 0, marginBottom: rem(16) }}>
            Samopoczucie
          </h3>
          <p style={{ fontSize: fontSize.sm, color: '#666666', margin: 0, marginBottom: rem(16) }}>
            Dane z {report.periodMetrics.wellbeingSummary.daysWithData} dni wpisów samopoczucia
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: rem(12) }}>
            {report.periodMetrics.wellbeingSummary.avgEnergy > 0 && (
              <div><span style={{ color: '#999999', fontSize: fontSize.xs }}>Energia</span> <strong>{(report.periodMetrics.wellbeingSummary.avgEnergy).toFixed(1)}/5</strong></div>
            )}
            {report.periodMetrics.wellbeingSummary.avgStress > 0 && (
              <div><span style={{ color: '#999999', fontSize: fontSize.xs }}>Stres</span> <strong>{(report.periodMetrics.wellbeingSummary.avgStress).toFixed(1)}/5</strong></div>
            )}
            {(report.periodMetrics.wellbeingSummary.avgSleepHours ?? 0) > 0 && (
              <div><span style={{ color: '#999999', fontSize: fontSize.xs }}>Sen (h)</span> <strong>{(report.periodMetrics.wellbeingSummary.avgSleepHours ?? 0).toFixed(1)}</strong></div>
            )}
            {(report.periodMetrics.wellbeingSummary.avgSleepQuality ?? 0) > 0 && (
              <div><span style={{ color: '#999999', fontSize: fontSize.xs }}>Jakość snu</span> <strong>{(report.periodMetrics.wellbeingSummary.avgSleepQuality ?? 0).toFixed(1)}/5</strong></div>
            )}
            {(report.periodMetrics.wellbeingSummary.heartburnDays ?? 0) > 0 && (
              <div><span style={{ color: '#999999', fontSize: fontSize.xs }}>Dni ze zgagą</span> <strong>{report.periodMetrics.wellbeingSummary.heartburnDays}</strong></div>
            )}
            {(report.periodMetrics.wellbeingSummary.nauseaDays ?? 0) > 0 && (
              <div><span style={{ color: '#999999', fontSize: fontSize.xs }}>Dni z nudnościami</span> <strong>{report.periodMetrics.wellbeingSummary.nauseaDays}</strong></div>
            )}
            {(report.periodMetrics.wellbeingSummary.jointPainDays ?? 0) > 0 && (
              <div><span style={{ color: '#999999', fontSize: fontSize.xs }}>Dni z bólem stawów</span> <strong>{report.periodMetrics.wellbeingSummary.jointPainDays}</strong></div>
            )}
          </div>
        </div>
      )}
      
      {/* Wyniki badań */}
      {report.periodMetrics.labResults && report.periodMetrics.labResults.length > 0 && (
        <div className="report-card" style={{
          background: '#FFFFFF',
          border: '1px solid #E5E5E5',
          borderRadius: rem(12),
          padding: rem(24),
          marginBottom: rem(24)
        }}>
          <h3 style={{ fontSize: fontSize.lg, fontWeight: 600, color: '#1A1A1A', margin: 0, marginBottom: rem(16) }}>
            Wyniki badań laboratoryjnych
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: rem(8) }}>
            {report.periodMetrics.labResults.map((lab, idx) => (
              <div key={idx} style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: rem(8),
                padding: rem(12),
                background: '#F9F9F9',
                borderRadius: rem(8)
              }}>
                <span style={{ fontWeight: 600, color: '#1A1A1A' }}>
                  {lab.type === 'inne' && lab.typeOther ? lab.typeOther : lab.type === 'kalprotectyna' ? 'Kalprotectyna' : lab.type === 'crp' ? 'CRP' : lab.type}
                </span>
                <span style={{ color: '#666666' }}>{format(parseISO(lab.date), 'dd.MM.yyyy', { locale: pl })}</span>
                <span style={{ color: '#6B7FFF', fontWeight: 600 }}>{lab.value} {lab.unit || ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Wykres trendu */}
      {chartData.length > 0 && (
        <div style={{
          background: '#FFFFFF',
          border: '1px solid #E5E5E5',
          borderRadius: rem(12),
          padding: rem(24),
          marginBottom: rem(24)
        }}>
          <div style={{ marginBottom: rem(20) }}>
            <h3 style={{ 
              fontSize: fontSize.lg, 
              fontWeight: 600, 
              color: '#1A1A1A', 
              margin: 0,
              marginBottom: rem(8)
            }}>
              Trend nasilenia objawów
            </h3>
            <p style={{
              fontSize: fontSize.sm,
              color: '#666666',
              margin: 0,
              marginBottom: rem(8),
              lineHeight: 1.5
            }}>
              Wskaźnik pokazujący, czy w ostatnim czasie objawy choroby nasilają się, słabną czy pozostają bez zmian. Trend wyliczany jest na podstawie porównania średniego nasilenia objawów z ostatnich dni.
            </p>
            <p style={{
              fontSize: fontSize.xs,
              color: '#999999',
              margin: 0,
              fontStyle: 'italic'
            }}>
              Wykres pokazuje dzienne nasilenie objawów, a powyżej widzisz stan dla całego okresu.
            </p>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
              <XAxis 
                dataKey="date" 
                stroke="#999999"
                style={{ fontSize: fontSize.xs }}
                interval={Math.floor(chartData.length / 10)}
              />
              <YAxis 
                domain={[0, 100]}
                stroke="#999999"
                style={{ fontSize: fontSize.xs }}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return (
                      <div style={{
                        background: '#FFFFFF',
                        border: '1px solid #E5E5E5',
                        borderRadius: rem(8),
                        padding: rem(12),
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}>
                        <div style={{ fontSize: fontSize.sm, fontWeight: 600, marginBottom: rem(8) }}>
                          {data.fullDate}
                        </div>
                        <div style={{ fontSize: fontSize.xs, color: '#999999' }}>
                          Score: <strong>{data.severityScore}</strong>
                        </div>
                        <div style={{ fontSize: fontSize.xs, color: '#999999' }}>
                          Wypróżnienia: <strong>{data.bmCount}</strong>
                        </div>
                        <div style={{ fontSize: fontSize.xs, color: '#999999' }}>
                          Ból: <strong>{data.painAvg.toFixed(1)}</strong>
                        </div>
                        <div style={{ fontSize: fontSize.xs, color: '#999999' }}>
                          Pilność: <strong>{data.urgencyAvg.toFixed(1)}</strong>
                        </div>
                        <div style={{ fontSize: fontSize.xs, color: '#999999' }}>
                          Śluz: <strong>{data.mucusPresent ? `TAK (max: ${data.mucusMax ?? '—'})` : 'NIE'}</strong>
                        </div>
                        <div style={{ fontSize: fontSize.xs, color: '#999999' }}>
                          Konsystencja płynna: <strong>{data.liquidPercent ?? 0}%</strong>
                        </div>
                        {data.hasBlood && (
                          <div style={{ fontSize: fontSize.xs, color: '#FF4444' }}>
                            Krew: <strong>{data.bloodMax || 'TAK'}</strong>
                          </div>
                        )}
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="severityScore" 
                stroke="#6B7FFF" 
                strokeWidth={2}
                dot={(props: any) => {
                  const { payload } = props;
                  return payload.hasBlood ? (
                    <Dot {...props} r={5} fill="#FF4444" stroke="#FF4444" strokeWidth={2} />
                  ) : (
                    <Dot {...props} r={3} fill="#6B7FFF" />
                  );
                }}
                name="Trend nasilenia objawów"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      
      {/* Lista dni (ostatnie 14 dni) */}
      {report.daySummaries.length > 0 && (
        <div style={{
          background: '#FFFFFF',
          border: '1px solid #E5E5E5',
          borderRadius: rem(12),
          padding: rem(24)
        }}>
          <h3 style={{ 
            fontSize: fontSize.lg, 
            fontWeight: 600, 
            color: '#1A1A1A', 
            margin: 0,
            marginBottom: rem(20)
          }}>
            Ostatnie dni
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: rem(12) }}>
            {report.daySummaries.slice(-14).reverse().map(day => (
              <div
                key={day.date}
                className="report-card"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                  gap: rem(16),
                  padding: rem(16),
                  background: '#F9F9F9',
                  borderRadius: rem(8),
                  border: '1px solid #E5E5E5'
                }}
              >
                <div>
                  <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(4) }}>
                    Data
                  </div>
                  <div style={{ fontSize: fontSize.base, fontWeight: 600, color: '#1A1A1A' }}>
                    {format(parseISO(day.date), 'dd.MM.yyyy', { locale: pl })}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(4) }}>
                    Trend
                  </div>
                  <div style={{ fontSize: fontSize.base, fontWeight: 600, color: '#1A1A1A' }}>
                    {day.severityScore}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(4) }}>
                    Wypróżnienia
                  </div>
                  <div style={{ fontSize: fontSize.base, fontWeight: 600, color: '#1A1A1A' }}>
                    {day.bmCount}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(4) }}>
                    Krew
                  </div>
                  <div style={{ 
                    fontSize: fontSize.base, 
                    fontWeight: 600, 
                    color: day.bloodPresentDay ? '#FF4444' : '#999999'
                  }}>
                    {day.bloodPresentDay ? `TAK${day.bloodMaxAmount ? ` (${day.bloodMaxAmount})` : ''}` : 'NIE'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(4) }}>
                    Ból avg
                  </div>
                  <div style={{ fontSize: fontSize.base, fontWeight: 600, color: '#1A1A1A' }}>
                    {day.painAvgDay > 0 ? day.painAvgDay.toFixed(1) : '—'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(4) }}>
                    Pilność avg
                  </div>
                  <div style={{ fontSize: fontSize.base, fontWeight: 600, color: '#1A1A1A' }}>
                    {day.urgencyAvgDay > 0 ? day.urgencyAvgDay.toFixed(1) : '—'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(4) }}>
                    Śluz
                  </div>
                  <div style={{ 
                    fontSize: fontSize.base, 
                    fontWeight: 600, 
                    color: day.mucusPresentDay ? '#6B7FFF' : '#999999'
                  }}>
                    {day.mucusPresentDay ? `TAK${day.mucusMaxAmount ? ` (${day.mucusMaxAmount})` : ''}` : 'NIE'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(4) }}>
                    Płynna %
                  </div>
                  <div style={{ fontSize: fontSize.base, fontWeight: 600, color: '#1A1A1A' }}>
                    {day.bmCount > 0 ? `${day.liquidBmPercent ?? 0}%` : '—'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
